/*!
 * TinyPoker Tax & Rebate (TR) 規則管理 — v2.25.0
 *
 * TR 級距本身就是資料：ratio 區間、rebate/tax、趴數全部在這頁維護，
 * calculator 不含任何級距常數。要對不同俱樂部做不同方案 → 開新規則、指派過去，不用改 code。
 *
 * 版面沿用「分潤規則管理」(TinyRevenueRules.js) 的 design token 與互動模式。
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Card, CardHeader, CardBody, CardTitle, Row, Col,
  Button, Table, Badge, Alert, Input, FormGroup, Label,
  Modal, ModalHeader, ModalBody, ModalFooter,
  InputGroup, InputGroupText,
} from "reactstrap";
import axios from "axios";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyStatStrip from "components/tiny/TinyStatStrip";
import TinyLoading, { TinyBusy } from "components/tiny/TinyLoading";
const API_BASE = "/api/tiny";

// ==================== 共用樣式（對齊 TinyClubConfig / TinyRevenueRules） ====================
const COLORS = {
  cardBg: '#1e2530', headerBg: '#252d3a', inputBg: '#1a1f2b',
  border: 'rgba(255,255,255,0.1)', borderInput: 'rgba(255,255,255,0.2)',
  textPrimary: '#fff', textSecondary: 'rgba(255,255,255,0.8)',
  textMuted: 'rgba(255,255,255,0.5)', textDimmed: 'rgba(255,255,255,0.4)',
  accent: '#667eea', accentGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  dangerGradient: 'linear-gradient(135deg, #f5365c 0%, #f56036 100%)',
  success: '#2dce89', danger: '#f5365c', info: '#11cdef', warning: '#fb6340',
};

const inputStyle = { background: COLORS.inputBg, color: COLORS.textPrimary, border: `1px solid ${COLORS.borderInput}`, borderRadius: '6px' };
const labelStyle = { color: COLORS.textSecondary, fontWeight: 500, fontSize: '13px' };
const modalHeaderStyle = { background: COLORS.headerBg, color: COLORS.textPrimary, borderBottom: `1px solid ${COLORS.border}` };
const modalBodyStyle = { background: COLORS.cardBg, color: COLORS.textPrimary };
const modalFooterStyle = { background: COLORS.headerBg, borderTop: `1px solid ${COLORS.border}` };
const cancelBtnStyle = { background: 'rgba(149,165,166,0.2)', border: '1px solid rgba(149,165,166,0.5)', color: '#95a5a6', padding: '8px 20px', borderRadius: '6px' };
const saveBtnStyle = (d) => ({ background: d ? 'rgba(102,126,234,0.5)' : COLORS.accentGradient, border: 'none', padding: '8px 30px', borderRadius: '6px', fontWeight: 600, boxShadow: '0 4px 12px rgba(102,126,234,0.3)' });

const thStyle = { borderColor: COLORS.border, padding: '12px 10px', fontWeight: 600, fontSize: '13px' };
const tdStyle = { borderColor: 'rgba(255,255,255,0.05)', padding: '12px 10px', verticalAlign: 'middle' };

const SectionCard = ({ color, icon, title, children, style }) => (
  <div style={{ background: `rgba(${color}, 0.08)`, border: `1px solid rgba(${color}, 0.25)`, borderRadius: '8px', padding: '18px', marginBottom: '20px', ...style }}>
    {title && <h6 style={{ color: `rgb(${color})`, marginBottom: '15px', fontSize: '14px', fontWeight: 600 }}>{icon && <i className={`fa fa-${icon} mr-2`} />}{title}</h6>}
    {children}
  </div>
);

const SCOPE_NOTE = "僅計 Ring Game（NLH / PLO / AOF），不含 MTT / SNG";

const AMOUNT_BASIS_OPTIONS = [
  { value: "pre_rake_pl_exclude_jp", label: "水前輸贏（扣除 JP）" },
  { value: "club_total_rake", label: "Ring Game 總水" },
];

const KIND_LABELS = {
  rebate: { label: "Rebate（補貼俱樂部）", short: "Rebate", color: COLORS.success, rgb: "45, 206, 137" },
  tax: { label: "Tax（向俱樂部抽取）", short: "Tax", color: COLORS.warning, rgb: "251, 99, 64" },
};

// 預設新規則：一個 rebate 級距 + 一個 tax 級距，讓使用者有個起點
const DEFAULT_RULE = {
  rule_key: "", rule_name: "", amount_basis: "pre_rake_pl_exclude_jp",
  tiers: [
    { min_ratio: null, max_ratio: 0, kind: "rebate", rate: 5 },
    { min_ratio: 0, max_ratio: null, kind: "tax", rate: 5 },
  ],
  description: "", notes: "",
};

const DEFAULT_TIER = { min_ratio: null, max_ratio: null, kind: "rebate", rate: 0 };

// 區間慣例：min_ratio < ratio <= max_ratio；null = 無界
function formatRatio(v) {
  if (v === null || v === undefined || v === "") return null;
  return Number(v);
}

function formatTierRange(tier) {
  const low = formatRatio(tier.min_ratio);
  const high = formatRatio(tier.max_ratio);
  if (low === null && high === null) return "全部";
  if (low === null) return `≤ ${high}`;
  if (high === null) return `> ${low}`;
  return `${low} ~ ${high}`;
}

function TinyTRRules() {
  const [rules, setRules] = useState([]);
  const [clubUsage, setClubUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingRule, setEditingRule] = useState({ ...DEFAULT_RULE });
  const [saving, setSaving] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  // ==================== 載入 ====================
  const loadRules = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API_BASE}/tr-rules`);
      if (res.data.code === 1) setRules(res.data.data.rules || []);
      else throw new Error(res.data.msg);
    } catch (err) {
      setError("載入 TR 規則失敗: " + (err.response?.data?.msg || err.message));
    } finally { setLoading(false); }
  }, []);

  // 每條規則有幾個俱樂部在用 —— 讓人知道改這條會影響誰
  const loadClubUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/clubs?status=`);
      if (res.data.code !== 1) return;
      const usage = {};
      (res.data.data.clubs || []).forEach((c) => {
        const key = c.tr_config?.rule_key;
        if (key) usage[key] = (usage[key] || 0) + 1;
      });
      setClubUsage(usage);
    } catch (err) {
      // 用量統計失敗不影響主功能
    }
  }, []);

  useEffect(() => { loadRules(); loadClubUsage(); }, [loadRules, loadClubUsage]);

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(null), 3000); return () => clearTimeout(t); } }, [success]);

  // ==================== CRUD ====================
  const handleCreate = () => {
    setEditingRule({ ...DEFAULT_RULE, tiers: DEFAULT_RULE.tiers.map(t => ({ ...t })) });
    setModalMode("create"); setModalOpen(true);
  };

  const handleEdit = (rule) => {
    setEditingRule({
      ...DEFAULT_RULE,
      ...rule,
      tiers: rule.tiers && rule.tiers.length > 0 ? rule.tiers.map(t => ({ ...t })) : [{ ...DEFAULT_TIER }],
    });
    setModalMode("edit"); setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      // 依 min_ratio 排序（null 視為負無窮，排最前面）
      const sortedTiers = [...editingRule.tiers].sort((a, b) => {
        const av = a.min_ratio === null || a.min_ratio === undefined ? -Infinity : Number(a.min_ratio);
        const bv = b.min_ratio === null || b.min_ratio === undefined ? -Infinity : Number(b.min_ratio);
        return av - bv;
      });
      const ruleData = { ...editingRule, tiers: sortedTiers };

      const res = modalMode === "create"
        ? await axios.post(`${API_BASE}/tr-rules`, ruleData)
        : await axios.put(`${API_BASE}/tr-rules/${editingRule.rule_key}`, ruleData);

      if (res.data.code === 1) {
        setSuccess(modalMode === "create" ? "TR 規則創建成功！" : "TR 規則更新成功！");
        setModalOpen(false); loadRules(); loadClubUsage();
      } else throw new Error(res.data.msg);
    } catch (err) { setError("保存失敗: " + (err.response?.data?.msg || err.message)); }
    finally { setSaving(false); }
  };

  const handleDeleteClick = (rule) => { setRuleToDelete(rule); setDeleteModalOpen(true); };

  const handleDelete = async () => {
    if (!ruleToDelete) return;
    setSaving(true);
    try {
      const res = await axios.delete(`${API_BASE}/tr-rules/${ruleToDelete.rule_key}`);
      if (res.data.code === 1) {
        setSuccess("TR 規則已刪除");
        setDeleteModalOpen(false); setRuleToDelete(null); loadRules(); loadClubUsage();
      } else throw new Error(res.data.msg);
    } catch (err) { setError("刪除失敗: " + (err.response?.data?.msg || err.message)); }
    finally { setSaving(false); }
  };

  // ==================== Tier 管理 ====================
  const addTier = () => {
    const tiers = [...editingRule.tiers, { ...DEFAULT_TIER }];
    setEditingRule(p => ({ ...p, tiers }));
  };

  const removeTier = (idx) => {
    if (editingRule.tiers.length <= 1) return;
    setEditingRule(p => ({ ...p, tiers: p.tiers.filter((_, i) => i !== idx) }));
  };

  const updateTier = (idx, field, value) => {
    const tiers = [...editingRule.tiers];
    const parsed = field === 'kind' ? value : (value === "" ? null : Number(value));
    tiers[idx] = { ...tiers[idx], [field]: parsed };
    setEditingRule(p => ({ ...p, tiers }));
  };

  // ==================== Render ====================
  if (loading && rules.length === 0) {
    return (
      <div className="content">
        <TinyLoading text="載入中" variant="page" />
      </div>
    );
  }

  return (
    <div className="content">
      {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
      {success && <Alert color="success" toggle={() => setSuccess(null)}>{success}</Alert>}

      {/* 標題原本埋在「頂部卡片」裡，跟統計數字擠在同一張卡。
          ⚠️「新增規則」是**動作** → 依公版的收容標準不進 header，
             改放在 header 正下方靠右，視覺位置幾乎不變。 */}
      <TinyPageHeader
        title="TR 規則"
        description={<>依「水前輸贏（扣除 JP）÷ Ring Game 總水」的比值決定補貼或抽成。{SCOPE_NOTE}</>}
      />

      {/* 同分潤規則：統計縮成一條，新增規則搬進列表卡片右上角。 */}
      <TinyStatStrip items={[
        { label: '規則總數', icon: 'fa-balance-scale', color: '102, 126, 234', iconColor: COLORS.accent, value: rules.length },
        { label: '含 Rebate 級距的規則', icon: 'fa-arrow-down', color: '45, 206, 137', iconColor: COLORS.success,
          value: rules.filter(r => (r.tiers || []).some(t => t.kind === 'rebate')).length },
        { label: '含 Tax 級距的規則', icon: 'fa-arrow-up', color: '251, 99, 64', iconColor: COLORS.warning,
          value: rules.filter(r => (r.tiers || []).some(t => t.kind === 'tax')).length },
      ]} />

      {/* 規則列表 */}
      <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
        <CardHeader style={{ background: COLORS.headerBg, borderBottom: `1px solid ${COLORS.border}` }}>
          <div className="d-flex justify-content-between align-items-center">
            <CardTitle tag="h5" style={{ color: COLORS.textPrimary, marginBottom: 0, fontWeight: 600 }}>
              <i className="fa fa-list mr-2" style={{ color: COLORS.accent }} />規則列表
            </CardTitle>
            {/* Brad 2026-09-10：新增規則跟刷新並排，不要獨立佔一行。 */}
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Button size="sm" onClick={() => { loadRules(); loadClubUsage(); }} disabled={loading}
                style={{ background: 'rgba(102,126,234,0.15)', border: '1px solid rgba(102,126,234,0.4)', color: COLORS.accent, padding: '6px 16px', borderRadius: '6px', fontWeight: 500 }}>
                {loading ? <TinyBusy light style={{ marginRight: '5px' }} /> : <i className="fa fa-refresh mr-1" />}刷新
              </Button>
              <Button size="sm" onClick={handleCreate}
                style={{ background: COLORS.accentGradient, border: 'none', padding: '6px 18px', borderRadius: '6px', fontWeight: 600 }}>
                <i className="fa fa-plus mr-1" />新增規則
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody style={{ padding: '20px' }}>
          {rules.length === 0 ? (
            <div className="text-center p-5">
              <i className="fa fa-balance-scale" style={{ fontSize: '4rem', color: 'rgba(255,255,255,0.12)', marginBottom: '20px', display: 'block' }} />
              <p style={{ color: COLORS.textMuted, fontSize: '1.1rem', marginBottom: '15px' }}>尚未設定 TR 規則</p>
              <Button onClick={handleCreate} style={{ background: COLORS.accentGradient, border: 'none', padding: '8px 20px', borderRadius: '6px' }}>
                <i className="fa fa-plus mr-2" />新增第一個規則
              </Button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table responsive hover size="sm" style={{ marginBottom: 0, color: COLORS.textSecondary }}>
                <thead style={{ background: COLORS.headerBg, color: COLORS.textPrimary }}>
                  <tr>
                    <th style={thStyle}>規則 Key</th>
                    <th style={thStyle}>規則名稱</th>
                    <th style={thStyle}>金額基數</th>
                    <th style={thStyle}>級距</th>
                    <th style={thStyle}>套用俱樂部</th>
                    <th style={thStyle}>說明</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <tr key={rule.rule_key} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                      <td style={tdStyle}>
                        <strong style={{ color: COLORS.textPrimary, fontSize: '13px' }}>{rule.rule_key}</strong>
                      </td>
                      <td style={tdStyle}><span style={{ fontWeight: 500 }}>{rule.rule_name}</span></td>
                      <td style={tdStyle}>
                        <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>
                          {(AMOUNT_BASIS_OPTIONS.find(o => o.value === (rule.amount_basis || 'pre_rake_pl_exclude_jp')) || {}).label}
                        </small>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {(rule.tiers || []).map((tier, idx) => {
                            const meta = KIND_LABELS[tier.kind] || KIND_LABELS.rebate;
                            return (
                              <Badge key={idx} style={{ background: `rgba(${meta.rgb},0.15)`, border: `1px solid rgba(${meta.rgb},0.4)`, color: meta.color, padding: '4px 8px', fontSize: '10px', fontWeight: 600 }}>
                                {formatTierRange(tier)} → {meta.short} {tier.rate}%
                              </Badge>
                            );
                          })}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <Badge style={{ background: clubUsage[rule.rule_key] ? 'rgba(17,205,239,0.2)' : 'rgba(255,255,255,0.06)', color: clubUsage[rule.rule_key] ? COLORS.info : COLORS.textDimmed, padding: '4px 10px', fontSize: '12px', fontWeight: 600 }}>
                          {clubUsage[rule.rule_key] || 0}
                        </Badge>
                      </td>
                      <td style={{ ...tdStyle, maxWidth: '200px' }}>
                        <small style={{ color: COLORS.textMuted }}>{rule.description?.substring(0, 40)}{rule.description?.length > 40 && '...'}</small>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Button size="sm" onClick={() => handleEdit(rule)} title="編輯"
                          style={{ background: 'rgba(17,205,239,0.15)', border: '1px solid rgba(17,205,239,0.4)', color: COLORS.info, padding: '6px 10px', borderRadius: '5px', marginRight: '4px' }}>
                          <i className="fa fa-edit" />
                        </Button>
                        <Button size="sm" onClick={() => handleDeleteClick(rule)} title="刪除"
                          style={{ background: 'rgba(245,54,92,0.15)', border: '1px solid rgba(245,54,92,0.4)', color: COLORS.danger, padding: '6px 10px', borderRadius: '5px' }}>
                          <i className="fa fa-trash" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          <div style={{ marginTop: '15px', color: COLORS.textMuted, fontSize: '13px' }}>
            共 {rules.length} 個 TR 規則 {loading && <TinyBusy text="更新中" style={{ marginLeft: 8 }} />}
          </div>
        </CardBody>
      </Card>

      {/* ==================== 新增/編輯 Modal ==================== */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg" style={{ marginTop: '30px' }}>
        <ModalHeader toggle={() => setModalOpen(false)} style={modalHeaderStyle}>
          <i className={`fa fa-${modalMode === "create" ? "plus-circle" : "edit"} mr-2`} style={{ color: COLORS.accent }} />
          {modalMode === "create" ? "新增 TR 規則" : `編輯規則 - ${editingRule.rule_key}`}
        </ModalHeader>
        <ModalBody style={{ ...modalBodyStyle, maxHeight: '70vh', overflowY: 'auto' }}>
          <SectionCard color="102, 126, 234" icon="info-circle" title="基本資訊">
            <Row>
              <Col md="6"><FormGroup>
                <Label style={labelStyle}>規則 Key <span style={{ color: COLORS.danger }}>*</span></Label>
                <Input type="text" value={editingRule.rule_key} onChange={(e) => setEditingRule(p => ({ ...p, rule_key: e.target.value }))}
                  disabled={modalMode === "edit"} placeholder="例如: standard" style={{ ...inputStyle, ...(modalMode === "edit" ? { opacity: 0.6 } : {}) }} />
                <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>唯一識別碼，使用英文與底線</small>
              </FormGroup></Col>
              <Col md="6"><FormGroup>
                <Label style={labelStyle}>規則名稱 <span style={{ color: COLORS.danger }}>*</span></Label>
                <Input type="text" value={editingRule.rule_name} onChange={(e) => setEditingRule(p => ({ ...p, rule_name: e.target.value }))} placeholder="例如: 標準 TR 階梯" style={inputStyle} />
              </FormGroup></Col>
            </Row>
            <Row>
              <Col md="6"><FormGroup style={{ marginBottom: 0 }}>
                <Label style={labelStyle}>金額基數</Label>
                <Input type="select" value={editingRule.amount_basis} onChange={(e) => setEditingRule(p => ({ ...p, amount_basis: e.target.value }))} style={inputStyle}>
                  {AMOUNT_BASIS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Input>
                <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>
                  趴數乘上去的對象。預設為水前輸贏本身（與雙週輸退獎池同口徑）
                </small>
              </FormGroup></Col>
            </Row>
          </SectionCard>

          {/* 級距配置 */}
          <SectionCard color="45, 206, 137" icon="bar-chart" title="級距配置">
            <div style={{ marginBottom: '15px' }}>
              <small style={{ color: COLORS.textMuted }}>
                ratio = 水前輸贏（扣除 JP）÷ Ring Game 總水。區間慣例為
                <strong style={{ color: COLORS.textSecondary }}> 下界 &lt; ratio ≤ 上界</strong>，
                留空代表無界（下界留空 = 負無窮、上界留空 = 正無窮）。
                <br />{SCOPE_NOTE}。
              </small>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <Table size="sm" style={{ marginBottom: '15px', color: COLORS.textSecondary }}>
                <thead style={{ background: COLORS.headerBg }}>
                  <tr>
                    <th style={{ ...thStyle, width: '50px', textAlign: 'center' }}>#</th>
                    <th style={thStyle}>ratio 下界</th>
                    <th style={thStyle}>ratio 上界</th>
                    <th style={thStyle}>類型</th>
                    <th style={thStyle}>趴數</th>
                    <th style={{ ...thStyle, width: '60px', textAlign: 'center' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {editingRule.tiers.map((tier, idx) => (
                    <tr key={idx} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                      <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, color: COLORS.textMuted }}>{idx + 1}</td>
                      <td style={tdStyle}>
                        <Input type="number" step="0.1" value={tier.min_ratio ?? ""} onChange={(e) => updateTier(idx, 'min_ratio', e.target.value)}
                          placeholder="負無窮" style={{ ...inputStyle, padding: '5px 10px', fontSize: '13px' }} />
                      </td>
                      <td style={tdStyle}>
                        <Input type="number" step="0.1" value={tier.max_ratio ?? ""} onChange={(e) => updateTier(idx, 'max_ratio', e.target.value)}
                          placeholder="正無窮" style={{ ...inputStyle, padding: '5px 10px', fontSize: '13px' }} />
                      </td>
                      <td style={tdStyle}>
                        <Input type="select" value={tier.kind || 'rebate'} onChange={(e) => updateTier(idx, 'kind', e.target.value)}
                          style={{ ...inputStyle, padding: '5px 10px', fontSize: '13px' }}>
                          <option value="rebate">Rebate（補貼）</option>
                          <option value="tax">Tax（抽取）</option>
                        </Input>
                      </td>
                      <td style={tdStyle}>
                        <InputGroup size="sm">
                          <Input type="number" min="0" max="100" value={tier.rate ?? ""} onChange={(e) => updateTier(idx, 'rate', e.target.value)}
                            style={{ ...inputStyle, padding: '5px 10px', fontSize: '13px' }} />
                          <InputGroupText style={{ background: COLORS.inputBg, color: COLORS.textMuted, border: `1px solid ${COLORS.borderInput}`, fontSize: '12px' }}>%</InputGroupText>
                        </InputGroup>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <Button size="sm" onClick={() => removeTier(idx)} disabled={editingRule.tiers.length <= 1}
                          style={{ background: 'rgba(245,54,92,0.15)', border: '1px solid rgba(245,54,92,0.3)', color: COLORS.danger, padding: '3px 8px', borderRadius: '4px', opacity: editingRule.tiers.length <= 1 ? 0.3 : 1 }}>
                          <i className="fa fa-minus" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <Button size="sm" onClick={addTier}
              style={{ background: 'rgba(45,206,137,0.15)', border: '1px solid rgba(45,206,137,0.4)', color: COLORS.success, padding: '6px 16px', borderRadius: '6px', fontWeight: 500 }}>
              <i className="fa fa-plus mr-1" /> 新增級距
            </Button>

            {/* 級距預覽 */}
            {editingRule.tiers.length > 0 && (
              <div style={{ marginTop: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '15px' }}>
                <h6 style={{ color: COLORS.textSecondary, fontSize: '13px', marginBottom: '10px' }}>
                  <i className="fa fa-eye mr-2" />級距預覽（由輸最多 → 贏最多）
                </h6>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {[...editingRule.tiers]
                    .sort((a, b) => {
                      const av = a.min_ratio === null || a.min_ratio === undefined ? -Infinity : Number(a.min_ratio);
                      const bv = b.min_ratio === null || b.min_ratio === undefined ? -Infinity : Number(b.min_ratio);
                      return av - bv;
                    })
                    .map((tier, idx) => {
                      const meta = KIND_LABELS[tier.kind] || KIND_LABELS.rebate;
                      return (
                        <div key={idx} style={{ background: `rgba(${meta.rgb}, 0.12)`, border: `1px solid rgba(${meta.rgb}, 0.3)`, borderRadius: '6px', padding: '8px 14px', fontSize: '12px' }}>
                          <div style={{ color: COLORS.textMuted }}>ratio {formatTierRange(tier)}</div>
                          <div style={{ color: meta.color, fontWeight: 600, fontSize: '14px' }}>{meta.short} {tier.rate}%</div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard color="251, 99, 64" icon="pencil" title="說明與備註" style={{ marginBottom: 0 }}>
            <FormGroup>
              <Label style={labelStyle}>說明</Label>
              <Input type="textarea" rows="2" value={editingRule.description || ""} onChange={(e) => setEditingRule(p => ({ ...p, description: e.target.value }))} placeholder="規則簡要說明..." style={inputStyle} />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={labelStyle}>備註</Label>
              <Input type="textarea" rows="3" value={editingRule.notes || ""} onChange={(e) => setEditingRule(p => ({ ...p, notes: e.target.value }))} placeholder="詳細備註..." style={inputStyle} />
            </FormGroup>
          </SectionCard>
        </ModalBody>
        <ModalFooter style={modalFooterStyle}>
          <Button onClick={() => setModalOpen(false)} style={cancelBtnStyle}>取消</Button>
          <Button onClick={handleSave} disabled={saving} style={saveBtnStyle(saving)}>
            {saving ? <><TinyBusy light style={{ marginRight: 8 }} />儲存中</> : <><i className="fa fa-check mr-2" />保存</>}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ==================== 刪除確認 Modal ==================== */}
      <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)} style={{ marginTop: '100px' }}>
        <ModalHeader toggle={() => setDeleteModalOpen(false)} style={modalHeaderStyle}>
          <i className="fa fa-exclamation-triangle mr-2" style={{ color: COLORS.danger }} />確認刪除
        </ModalHeader>
        <ModalBody style={modalBodyStyle}>
          {ruleToDelete && (
            <div>
              <div style={{ background: 'rgba(245,54,92,0.1)', border: '1px solid rgba(245,54,92,0.3)', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                <p style={{ marginBottom: 0, color: COLORS.textSecondary }}>
                  <i className="fa fa-warning mr-2" style={{ color: COLORS.danger }} />
                  確定要刪除 TR 規則 <strong style={{ color: COLORS.textPrimary }}>{ruleToDelete.rule_name}</strong> 嗎？
                </p>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '15px' }}>
                <div style={{ marginBottom: '8px' }}><strong style={{ color: COLORS.textSecondary, fontSize: '13px' }}>規則 Key：</strong><span style={{ marginLeft: '10px' }}>{ruleToDelete.rule_key}</span></div>
                <div><strong style={{ color: COLORS.textSecondary, fontSize: '13px' }}>級距數：</strong><span style={{ marginLeft: '10px' }}>{ruleToDelete.tiers?.length || 0}</span></div>
              </div>
              <div style={{ marginTop: '15px', background: 'rgba(251,99,64,0.08)', border: '1px solid rgba(251,99,64,0.2)', borderRadius: '8px', padding: '12px' }}>
                <small style={{ color: COLORS.warning }}>
                  <i className="fa fa-info-circle mr-2" />
                  如果此規則正被俱樂部使用、或被設為全域預設規則，將無法刪除。
                </small>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter style={modalFooterStyle}>
          <Button onClick={() => setDeleteModalOpen(false)} style={cancelBtnStyle}>取消</Button>
          <Button onClick={handleDelete} disabled={saving}
            style={{ background: COLORS.dangerGradient, border: 'none', padding: '8px 25px', borderRadius: '6px', fontWeight: 600, boxShadow: '0 4px 12px rgba(245,54,92,0.3)' }}>
            {saving ? <><TinyBusy light style={{ marginRight: 8 }} />刪除中</> : <><i className="fa fa-trash mr-2" />確認刪除</>}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default TinyTRRules;
